import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import ScheduleSessionsPanel, { ScheduleSessionItem, SessionStatus, UpdateScheduleSessionRequest } from './components/ScheduleSessionsPanel';
import ScheduleSessionsCalendar from './components/ScheduleSessionsCalendar';

type RecurrenceType = 'None' | 'Daily' | 'Weekly' | 'Monthly';
type LocationType = 'InPerson' | 'Online';
type TargetType = 'Classroom' | 'Students';
type SessionQuickFilter = 'All' | 'Upcoming' | 'Past' | 'Today' | 'Modified';
type SessionTargetFilter = 'All' | 'Classroom' | 'Students';

interface RecurrenceRuleState {
  interval: string;
  weekDays: number[];
  monthDay: string;
}

interface Classroom {
  id: number;
  name: string;
}

interface UserOption {
  userId: number;
  displayName: string;
  email?: string | null;
}

interface TeachingAssignmentOption {
  id: number;
  teacherUserId: number;
  teacherDisplayName: string;
  studentUserId: number;
  studentDisplayName: string;
  courseId: number;
  courseName: string;
  status: string;
}

interface ScheduleDetail {
  id: number;
  title: string;
  classroomId?: number | null;
  teacherUserId: number;
  teachingAssignmentId?: number | null;
  teachingAssignmentLabel?: string | null;
  targetType?: TargetType;
  studentUserIds?: number[];
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  recurrenceType: RecurrenceType;
  recurrenceRule?: string | null;
  locationType: LocationType;
  location?: string | null;
  meetingUrl?: string | null;
  notes?: string | null;
  status?: string;
  description?: string | null;
  isActive?: boolean;
  classroomName?: string;
  teacherDisplayName?: string;
  sessions?: ScheduleSessionItem[];
}

interface ScheduleFormState {
  title: string;
  targetType: TargetType;
  classroomId: string;
  teachingAssignmentId: string;
  teacherUserId: string;
  studentUserIds: number[];
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  recurrenceType: RecurrenceType;
  recurrenceRule: RecurrenceRuleState | null;
  locationType: LocationType;
  location: string;
  meetingUrl: string;
  notes: string;
  description: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const emptyForm: ScheduleFormState = {
  title: '',
  targetType: 'Classroom',
  classroomId: '',
  teachingAssignmentId: '',
  teacherUserId: '',
  studentUserIds: [],
  startDate: '',
  endDate: '',
  startTime: '09:00',
  endTime: '10:00',
  recurrenceType: 'None',
  recurrenceRule: null,
  locationType: 'InPerson',
  location: '',
  meetingUrl: '',
  notes: '',
  description: '',
};

const toDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const [hours, minutes] = trimmed.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }

  const date = new Date(`2000-01-01T${trimmed}`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const getFallbackDate = (value?: string): Date => {
  const date = value ? new Date(`${value}T00:00:00`) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const getDefaultWeekDays = (startDate?: string): number[] => [getFallbackDate(startDate).getDay()];

const getDefaultMonthDay = (startDate?: string): string => String(getFallbackDate(startDate).getDate());

const parseRecurrenceRule = (value?: string | null, recurrenceType?: RecurrenceType, startDate?: string): RecurrenceRuleState => {
  const defaults: RecurrenceRuleState = {
    interval: '1',
    weekDays: recurrenceType === 'Weekly' ? getDefaultWeekDays(startDate) : [],
    monthDay: recurrenceType === 'Monthly' ? getDefaultMonthDay(startDate) : '',
  };

  if (!value) {
    return defaults;
  }

  try {
    const parsed = JSON.parse(value) as Partial<RecurrenceRuleState>;
    const interval = Math.max(1, Number(parsed?.interval ?? 1));
    const weekDays = Array.isArray(parsed?.weekDays)
      ? parsed.weekDays.map((day) => Number(day)).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
      : [];
    const monthDayValue = Number(parsed?.monthDay);
    return {
      interval: String(interval),
      weekDays: recurrenceType === 'Weekly' ? (weekDays.length > 0 ? weekDays : getDefaultWeekDays(startDate)) : [],
      monthDay: recurrenceType === 'Monthly'
        ? String(Number.isInteger(monthDayValue) && monthDayValue >= 1 && monthDayValue <= 31 ? monthDayValue : Number(getDefaultMonthDay(startDate)))
        : '',
    };
  } catch {
    return defaults;
  }
};

const buildRecurrenceRule = (form: ScheduleFormState): string | null => {
  if (form.recurrenceType === 'None') {
    return null;
  }

  const interval = Math.max(1, Number(form.recurrenceRule?.interval || 1));
  const rule: Record<string, unknown> = { interval };

  if (form.recurrenceType === 'Weekly') {
    rule.weekDays = form.recurrenceRule?.weekDays?.length
      ? form.recurrenceRule.weekDays
      : getDefaultWeekDays(form.startDate);
  }

  if (form.recurrenceType === 'Monthly') {
    const monthDay = Number(form.recurrenceRule?.monthDay || getDefaultMonthDay(form.startDate));
    rule.monthDay = Math.min(31, Math.max(1, Number.isFinite(monthDay) ? monthDay : Number(getDefaultMonthDay(form.startDate))));
  }

  return JSON.stringify(rule);
};

const mapScheduleToForm = (schedule: ScheduleDetail): ScheduleFormState => ({
  title: schedule.title || '',
  targetType: schedule.targetType === 'Students' ? 'Students' : 'Classroom',
  classroomId: schedule.classroomId ? String(schedule.classroomId) : '',
  teachingAssignmentId: schedule.teachingAssignmentId ? String(schedule.teachingAssignmentId) : '',
  teacherUserId: schedule.teacherUserId ? String(schedule.teacherUserId) : '',
  studentUserIds: Array.isArray(schedule.studentUserIds) ? schedule.studentUserIds.filter((item) => Number.isFinite(item) && item > 0) : [],
  startDate: toDateInput(schedule.startDate),
  endDate: toDateInput(schedule.endDate),
  startTime: toTimeInput(schedule.startTime),
  endTime: toTimeInput(schedule.endTime),
  recurrenceType: schedule.recurrenceType === 'Daily' || schedule.recurrenceType === 'Weekly' || schedule.recurrenceType === 'Monthly'
    ? schedule.recurrenceType
    : 'None',
  recurrenceRule: schedule.recurrenceType === 'None'
    ? null
    : parseRecurrenceRule(schedule.recurrenceRule, schedule.recurrenceType, toDateInput(schedule.startDate)),
  locationType: schedule.locationType === 'Online' ? 'Online' : 'InPerson',
  location: schedule.location || '',
  meetingUrl: schedule.meetingUrl || '',
  notes: schedule.notes || '',
  description: schedule.description || '',
});

const validateForm = (form: ScheduleFormState): string | null => {
  if (form.targetType === 'Classroom' && !form.classroomId) {
    return 'Classroom is required for classroom schedules.';
  }

  if (form.targetType === 'Students' && form.studentUserIds.length === 0) {
    return 'Select at least one student for individual or multi-student schedules.';
  }

  if (!form.teacherUserId) {
    return 'Teacher is required.';
  }

  if (!form.title.trim()) {
    return 'Title is required.';
  }

  if (!form.startDate || !form.endDate || !form.startTime || !form.endTime) {
    return 'Start date, end date, start time, and end time are required.';
  }

  if (form.recurrenceType !== 'None') {
    const interval = Number(form.recurrenceRule?.interval || 1);
    if (!Number.isInteger(interval) || interval < 1) {
      return 'Recurrence interval must be at least 1.';
    }

    if (form.recurrenceType === 'Weekly' && (form.recurrenceRule?.weekDays || []).length === 0) {
      return 'Select at least one weekday for weekly schedules.';
    }

    if (form.recurrenceType === 'Monthly') {
      const monthDay = Number(form.recurrenceRule?.monthDay);
      if (!Number.isInteger(monthDay) || monthDay < 1 || monthDay > 31) {
        return 'Select a valid day of month between 1 and 31.';
      }
    }
  }

  const start = new Date(`${form.startDate}T${form.startTime}:00`);
  const end = new Date(`${form.endDate}T${form.endTime}:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Invalid date/time values.';
  }

  if (end <= start) {
    return 'End time must be after the start time.';
  }

  if (form.locationType === 'InPerson' && !form.location.trim()) {
    return 'Location is required for in-person schedules.';
  }

  if (form.locationType === 'Online' && !form.meetingUrl.trim()) {
    return 'Meeting URL is required for online schedules.';
  }

  return null;
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const toDayKey = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ManageScheduleDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [teachers, setTeachers] = useState<UserOption[]>([]);
  const [students, setStudents] = useState<UserOption[]>([]);
  const [teachingAssignments, setTeachingAssignments] = useState<TeachingAssignmentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState<ScheduleFormState>(emptyForm);
  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'sessions' | 'calendar'>('details');
  const [sessionDataLoaded, setSessionDataLoaded] = useState(false);
  const [sessionQuickFilter, setSessionQuickFilter] = useState<SessionQuickFilter>('All');
  const [sessionStatusFilter, setSessionStatusFilter] = useState<'All' | SessionStatus>('All');
  const [sessionTargetFilter, setSessionTargetFilter] = useState<SessionTargetFilter>('All');
  const [sessionSearchText, setSessionSearchText] = useState('');
  const [sessionFromDate, setSessionFromDate] = useState('');
  const [sessionToDate, setSessionToDate] = useState('');

  const isCreateMode = id === 'new' || !id;
  const isEditMode = !isCreateMode;

  const loadLookups = async (token: string) => {
    const headers = getOrganizationHeaders(token);
    const [classroomResponse, organizationUserResponse, teachingAssignmentResponse] = await Promise.all([
      axios.get<Classroom[]>(`${API_BASE_URL}/classrooms`, { headers }),
      axios.get<UserOption[]>(`${API_BASE_URL}/classrooms/users`, { headers }),
      axios.get<TeachingAssignmentOption[]>(`${API_BASE_URL}/teachingassignments?status=Active`, { headers }),
    ]);

    const orgUsers = Array.isArray(organizationUserResponse.data) ? organizationUserResponse.data : [];
    setClassrooms(Array.isArray(classroomResponse.data) ? classroomResponse.data : []);
    setTeachers(orgUsers);
    setStudents(orgUsers);
    setTeachingAssignments(Array.isArray(teachingAssignmentResponse.data) ? teachingAssignmentResponse.data : []);
  };

  const loadScheduleDetail = async (token: string, includeSessions: boolean = false) => {
    if (isCreateMode || !id) {
      setSchedule(null);
      setForm(emptyForm);
      setSessionDataLoaded(false);
      return;
    }

    const headers = getOrganizationHeaders(token);
    const response = await axios.get<ScheduleDetail>(`${API_BASE_URL}/schedules/${id}?includeSessions=${includeSessions}`, { headers });
    const detail = response.data;
    setSchedule(detail);
    setForm(mapScheduleToForm(detail));
    setSessionDataLoaded(includeSessions || Boolean(detail.sessions && detail.sessions.length > 0));
  };

  useEffect(() => {
    const run = async () => {
      if (!hasActiveOrganization) {
        setError('No organization selected. Please switch organization first.');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = await getManagementToken();
        await loadLookups(token);

        await loadScheduleDetail(token, false);
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load schedule data.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, id, isCreateMode]);

  const updateField = (field: keyof ScheduleFormState, value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const updateTeachingAssignmentSelection = (value: string) => {
    setForm((previous) => {
      if (!value) {
        return {
          ...previous,
          teachingAssignmentId: '',
        };
      }

      const selectedAssignment = teachingAssignments.find((item) => String(item.id) === value);
      if (!selectedAssignment) {
        return previous;
      }

      return {
        ...previous,
        targetType: 'Students',
        classroomId: '',
        teachingAssignmentId: value,
        teacherUserId: String(selectedAssignment.teacherUserId),
        studentUserIds: [selectedAssignment.studentUserId],
      };
    });
  };

  const updateRecurrenceType = (nextType: RecurrenceType) => {
    setForm((previous) => ({
      ...previous,
      recurrenceType: nextType,
      recurrenceRule: nextType === 'None'
        ? null
        : nextType === 'Weekly'
          ? {
              interval: previous.recurrenceRule?.interval || '1',
              weekDays: previous.recurrenceRule?.weekDays?.length ? previous.recurrenceRule.weekDays : getDefaultWeekDays(previous.startDate),
              monthDay: '',
            }
          : nextType === 'Monthly'
            ? {
                interval: previous.recurrenceRule?.interval || '1',
                weekDays: [],
                monthDay: previous.recurrenceRule?.monthDay || getDefaultMonthDay(previous.startDate),
              }
            : previous.recurrenceRule,
    }));
  };

  const updateRecurrenceInterval = (nextInterval: string) => {
    setForm((previous) => ({
      ...previous,
      recurrenceRule: {
        ...(previous.recurrenceRule || { interval: '1', weekDays: [], monthDay: '' }),
        interval: nextInterval,
      },
    }));
  };

  const toggleRecurrenceWeekDay = (day: number) => {
    setForm((previous) => {
      const currentRule = previous.recurrenceRule || { interval: '1', weekDays: getDefaultWeekDays(previous.startDate), monthDay: '' };
      const currentDays = Array.isArray(currentRule.weekDays) ? currentRule.weekDays.map((value) => Number(value)).filter((value) => Number.isInteger(value)) : getDefaultWeekDays(previous.startDate);
      const nextDays = currentDays.includes(day) ? currentDays.filter((value: number) => value !== day) : [...currentDays, day];
      return {
        ...previous,
        recurrenceRule: {
          ...currentRule,
          interval: String(currentRule.interval || '1'),
          weekDays: nextDays,
        },
      };
    });
  };

  const updateRecurrenceMonthDay = (nextMonthDay: string) => {
    setForm((previous) => ({
      ...previous,
      recurrenceRule: {
        ...(previous.recurrenceRule || { interval: '1', weekDays: [], monthDay: '' }),
        monthDay: nextMonthDay,
      },
    }));
  };

  const handleStartDateChange = (value: string) => {
    setForm((previous) => {
      const nextRule = previous.recurrenceRule || { interval: '1', weekDays: [], monthDay: '' };
      if (previous.recurrenceType === 'Weekly' && (!Array.isArray(nextRule.weekDays) || nextRule.weekDays.length === 0)) {
        nextRule.weekDays = getDefaultWeekDays(value);
      }
      if (previous.recurrenceType === 'Monthly' && !nextRule.monthDay) {
        nextRule.monthDay = getDefaultMonthDay(value);
      }

      return {
        ...previous,
        startDate: value,
        recurrenceRule: previous.recurrenceType === 'None' ? previous.recurrenceRule : { ...nextRule },
      };
    });
  };

  const toggleStudentSelection = (userId: number) => {
    setForm((previous) => ({
      ...previous,
      studentUserIds: previous.studentUserIds.includes(userId)
        ? previous.studentUserIds.filter((item) => item !== userId)
        : [...previous.studentUserIds, userId],
    }));
  };

  const onSubmit = async () => {
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const payload = {
        classroomId: form.targetType === 'Classroom' && form.classroomId ? Number(form.classroomId) : null,
        teacherUserId: Number(form.teacherUserId),
        teachingAssignmentId: form.targetType === 'Students' && form.teachingAssignmentId ? Number(form.teachingAssignmentId) : null,
        targetType: form.targetType,
        studentUserIds: form.targetType === 'Students' ? form.studentUserIds : [],
        title: form.title.trim(),
        description: form.description.trim() || null,
        startDate: new Date(`${form.startDate}T00:00:00Z`).toISOString(),
        endDate: new Date(`${form.endDate}T00:00:00Z`).toISOString(),
        startTime: form.startTime,
        endTime: form.endTime,
        recurrenceType: form.recurrenceType,
        recurrenceRule: buildRecurrenceRule(form),
        locationType: form.locationType,
        location: form.locationType === 'InPerson' ? form.location.trim() : null,
        meetingUrl: form.locationType === 'Online' ? form.meetingUrl.trim() : null,
        notes: form.notes.trim() || null,
        status: 'Scheduled',
        isActive: true,
      };

      if (isCreateMode) {
        await axios.post(`${API_BASE_URL}/schedules`, payload, { headers });
        setMessage('Schedule series created successfully.');
        navigate('/manage-schedule');
      } else if (id) {
        await axios.put(`${API_BASE_URL}/schedules/${id}`, payload, { headers });
        setMessage('Schedule updated successfully.');
        navigate('/manage-schedule');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save schedule.');
    } finally {
      setSaving(false);
    }
  };

  const onUpdateSession = async (sessionId: number, payload: UpdateScheduleSessionRequest) => {
    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);

    await axios.put(`${API_BASE_URL}/schedules/sessions/${sessionId}`, payload, { headers });
    await loadScheduleDetail(token, true);
    setMessage('Session updated successfully.');
  };

  const onStartSession = async (sessionId: number) => {
    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);

    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/start`, {}, { headers });
    await loadScheduleDetail(token, true);
    setMessage('Session started successfully.');
  };

  const onCompleteSession = async (sessionId: number) => {
    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);

    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/complete`, {}, { headers });
    await loadScheduleDetail(token, true);
    setMessage('Session completed successfully.');
  };

  const onRecordAttendance = async (sessionId: number, payload: { userId: number; status: string; joinedAt?: string | null; leftAt?: string | null; notes?: string | null; }) => {
    const token = await getManagementToken();
    const headers = getOrganizationHeaders(token);

    await axios.post(`${API_BASE_URL}/schedules/sessions/${sessionId}/attendance`, payload, { headers });
    await loadScheduleDetail(token, true);
    setMessage('Attendance recorded successfully.');
  };

  const ensureSessionDataLoaded = async (nextTab?: 'sessions' | 'calendar') => {
    if (isCreateMode || !id || sessionDataLoaded) {
      if (nextTab) {
        setActiveTab(nextTab);
      }
      return;
    }

    try {
      const token = await getManagementToken();
      await loadScheduleDetail(token, true);
    } finally {
      if (nextTab) {
        setActiveTab(nextTab);
      }
    }
  };

  const title = isCreateMode ? 'Create Schedule' : 'Update Schedule';

  const teacherOptions = useMemo(() => teachers.map((teacher) => ({
    value: String(teacher.userId),
    label: teacher.displayName || teacher.email || `User ${teacher.userId}`,
  })), [teachers]);

  const studentOptions = useMemo(() => students.map((student) => ({
    value: String(student.userId),
    label: student.displayName || student.email || `Student ${student.userId}`,
  })), [students]);

  const classroomOptions = useMemo(() => classrooms.map((classroom) => ({
    value: String(classroom.id),
    label: classroom.name,
  })), [classrooms]);

  const allSessions = useMemo(() => schedule?.sessions || [], [schedule?.sessions]);

  const filteredSessions = useMemo(() => {
    const now = new Date();
    const todayKey = toDayKey(now.toISOString());
    const search = sessionSearchText.trim().toLowerCase();

    return allSessions.filter((session) => {
      const start = new Date(session.startDateTime);
      if (Number.isNaN(start.getTime())) {
        return false;
      }

      if (sessionQuickFilter === 'Upcoming' && start < now) {
        return false;
      }

      if (sessionQuickFilter === 'Past' && start >= now) {
        return false;
      }

      if (sessionQuickFilter === 'Today' && toDayKey(session.startDateTime) !== todayKey) {
        return false;
      }

      if (sessionQuickFilter === 'Modified' && !session.isModified) {
        return false;
      }

      if (sessionStatusFilter !== 'All' && session.status !== sessionStatusFilter) {
        return false;
      }

      if (sessionTargetFilter !== 'All') {
        const targetType = (session.targetType || '').toLowerCase();
        const expected = sessionTargetFilter.toLowerCase();
        if (targetType !== expected) {
          return false;
        }
      }

      const dayKey = toDayKey(session.startDateTime);
      if (sessionFromDate && dayKey < sessionFromDate) {
        return false;
      }

      if (sessionToDate && dayKey > sessionToDate) {
        return false;
      }

      if (search) {
        const haystack = [
          session.classroomName,
          session.studentDisplayName,
          session.teacherDisplayName,
          session.location,
          session.meetingUrl,
          session.notes,
          session.status,
        ].join(' ').toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      return true;
    });
  }, [allSessions, sessionFromDate, sessionQuickFilter, sessionSearchText, sessionStatusFilter, sessionTargetFilter, sessionToDate]);

  const clearSessionFilters = () => {
    setSessionQuickFilter('All');
    setSessionStatusFilter('All');
    setSessionTargetFilter('All');
    setSessionSearchText('');
    setSessionFromDate('');
    setSessionToDate('');
  };

  return (
    <div style={{ width: '100%', display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Administration', to: '/analytics' },
        { label: 'Schedule', to: '/manage-schedule' },
        { label: isCreateMode ? 'New' : 'Detail', isCurrent: true },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: '#173159' }}>{title}</h2>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {isCreateMode ? 'Add a new recurring schedule series' : schedule ? `Editing: ${schedule.title}` : 'Loading schedule...'}
          </div>
        </div>

        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => navigate('/manage-schedule')}>
          Back to list
        </button>
      </div>

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {(error || message) && (
        <div style={{ borderRadius: 10, border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`, background: error ? '#fff1f2' : '#eff6ff', color: error ? '#b91c1c' : '#1d4ed8', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error || message}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, color: '#475569' }}>Loading schedule details...</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {isEditMode && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="amd-btn"
                onClick={() => setActiveTab('details')}
                style={{
                  background: activeTab === 'details' ? '#1d4ed8' : '#fff',
                  color: activeTab === 'details' ? '#fff' : '#1e293b',
                  border: `1px solid ${activeTab === 'details' ? '#1d4ed8' : '#dfe8f8'}`,
                }}
              >
                Series Details
              </button>
              <button
                type="button"
                className="amd-btn"
                onClick={() => { void ensureSessionDataLoaded('sessions'); }}
                style={{
                  background: activeTab === 'sessions' ? '#1d4ed8' : '#fff',
                  color: activeTab === 'sessions' ? '#fff' : '#1e293b',
                  border: `1px solid ${activeTab === 'sessions' ? '#1d4ed8' : '#dfe8f8'}`,
                }}
              >
                Sessions ({schedule?.sessions?.length || 0})
              </button>
              <button
                type="button"
                className="amd-btn"
                onClick={() => { void ensureSessionDataLoaded('calendar'); }}
                style={{
                  background: activeTab === 'calendar' ? '#1d4ed8' : '#fff',
                  color: activeTab === 'calendar' ? '#fff' : '#1e293b',
                  border: `1px solid ${activeTab === 'calendar' ? '#1d4ed8' : '#dfe8f8'}`,
                }}
              >
                Calendar ({filteredSessions.length})
              </button>
            </div>
          )}

          {isEditMode && (activeTab === 'sessions' || activeTab === 'calendar') && (
            <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 12, display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, color: '#173159' }}>Session Filters</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{filteredSessions.length} of {allSessions.length}</span>
                  <button type="button" className="amd-btn amd-btn-ghost" onClick={clearSessionFilters}>Clear</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
                <button type="button" className="amd-btn" onClick={() => setSessionQuickFilter('All')} style={{ background: sessionQuickFilter === 'All' ? '#1d4ed8' : '#fff', color: sessionQuickFilter === 'All' ? '#fff' : '#1e293b', border: `1px solid ${sessionQuickFilter === 'All' ? '#1d4ed8' : '#dfe8f8'}` }}>All</button>
                <button type="button" className="amd-btn" onClick={() => setSessionQuickFilter('Upcoming')} style={{ background: sessionQuickFilter === 'Upcoming' ? '#1d4ed8' : '#fff', color: sessionQuickFilter === 'Upcoming' ? '#fff' : '#1e293b', border: `1px solid ${sessionQuickFilter === 'Upcoming' ? '#1d4ed8' : '#dfe8f8'}` }}>Upcoming</button>
                <button type="button" className="amd-btn" onClick={() => setSessionQuickFilter('Past')} style={{ background: sessionQuickFilter === 'Past' ? '#1d4ed8' : '#fff', color: sessionQuickFilter === 'Past' ? '#fff' : '#1e293b', border: `1px solid ${sessionQuickFilter === 'Past' ? '#1d4ed8' : '#dfe8f8'}` }}>Past</button>
                <button type="button" className="amd-btn" onClick={() => setSessionQuickFilter('Today')} style={{ background: sessionQuickFilter === 'Today' ? '#1d4ed8' : '#fff', color: sessionQuickFilter === 'Today' ? '#fff' : '#1e293b', border: `1px solid ${sessionQuickFilter === 'Today' ? '#1d4ed8' : '#dfe8f8'}` }}>Today</button>
                <button type="button" className="amd-btn" onClick={() => setSessionQuickFilter('Modified')} style={{ background: sessionQuickFilter === 'Modified' ? '#1d4ed8' : '#fff', color: sessionQuickFilter === 'Modified' ? '#fff' : '#1e293b', border: `1px solid ${sessionQuickFilter === 'Modified' ? '#1d4ed8' : '#dfe8f8'}` }}>Modified</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Search</span>
                  <input
                    type="text"
                    value={sessionSearchText}
                    onChange={(event) => setSessionSearchText(event.target.value)}
                    placeholder="Teacher, student, class, notes..."
                  />
                </label>

                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Status</span>
                  <select value={sessionStatusFilter} onChange={(event) => setSessionStatusFilter(event.target.value as 'All' | SessionStatus)}>
                    <option value="All">All</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Rescheduled">Rescheduled</option>
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Target</span>
                  <select value={sessionTargetFilter} onChange={(event) => setSessionTargetFilter(event.target.value as SessionTargetFilter)}>
                    <option value="All">All</option>
                    <option value="Classroom">Classroom</option>
                    <option value="Students">Students</option>
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>From</span>
                  <input type="date" value={sessionFromDate} onChange={(event) => setSessionFromDate(event.target.value)} />
                </label>

                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>To</span>
                  <input type="date" value={sessionToDate} onChange={(event) => setSessionToDate(event.target.value)} />
                </label>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(260px, 0.8fr)', gap: 16 }}>
            <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 18 }}>
              {activeTab === 'details' && (
                <div style={{ display: 'grid', gap: 14 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Title</span>
                <input type="text" value={form.title} onChange={(event) => updateField('title', event.target.value)} />
              </label>

              <div style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Schedule Target</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {(['Classroom', 'Students'] as TargetType[]).map((targetType) => {
                    const isSelected = form.targetType === targetType;
                    return (
                      <button
                        key={targetType}
                        type="button"
                        onClick={() => {
                          setForm((previous) => ({
                            ...previous,
                            targetType,
                            classroomId: targetType === 'Classroom' ? previous.classroomId : '',
                            teachingAssignmentId: targetType === 'Students' ? previous.teachingAssignmentId : '',
                          }));
                        }}
                        style={{
                          border: `1px solid ${isSelected ? '#2563eb' : '#dfe8f8'}`,
                          background: isSelected ? '#eff6ff' : '#fff',
                          color: isSelected ? '#1d4ed8' : '#334155',
                          borderRadius: 10,
                          padding: '10px 12px',
                          fontWeight: isSelected ? 700 : 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {targetType === 'Classroom' ? 'Classroom' : 'Individual / Multiple Students'}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Teacher</span>
                  <select value={form.teacherUserId} onChange={(event) => updateField('teacherUserId', event.target.value)} disabled={form.targetType === 'Students' && !!form.teachingAssignmentId}>
                    <option value="">Select teacher</option>
                    {teacherOptions.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {form.targetType === 'Classroom' ? (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Classroom</span>
                  <select value={form.classroomId} onChange={(event) => updateField('classroomId', event.target.value)}>
                    <option value="">Select classroom</option>
                    {classroomOptions.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Teaching Assignment</span>
                    <select value={form.teachingAssignmentId} onChange={(event) => updateTeachingAssignmentSelection(event.target.value)}>
                      <option value="">Optional: select one-to-one assignment</option>
                      {teachingAssignments.map((assignment) => (
                        <option key={assignment.id} value={assignment.id}>
                          {`${assignment.teacherDisplayName} -> ${assignment.studentDisplayName} (${assignment.courseName})`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Students</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{form.teachingAssignmentId ? 'Bound to selected teaching assignment' : 'Select one or many learners not attached to a class'}</span>
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #dfe8f8', borderRadius: 10, padding: 8, display: 'grid', gap: 6, background: '#f8fafc' }}>
                    {studentOptions.length === 0 ? (
                      <div style={{ color: '#64748b', fontSize: 13 }}>No organization members available for selection.</div>
                    ) : (
                      studentOptions.map((student) => {
                        const userId = Number(student.value);
                        const isSelected = form.studentUserIds.includes(userId);
                        return (
                          <label key={student.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e293b', cursor: 'pointer', background: isSelected ? '#e0f2fe' : 'transparent', borderRadius: 8, padding: '6px 8px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!!form.teachingAssignmentId}
                              onChange={() => toggleStudentSelection(userId)}
                            />
                            <span>{student.label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    <strong>{form.studentUserIds.length}</strong> selected for this schedule
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Start Date</span>
                  <input type="date" value={form.startDate} onChange={(event) => handleStartDateChange(event.target.value)} />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>End Date</span>
                  <input type="date" value={form.endDate} onChange={(event) => updateField('endDate', event.target.value)} />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Start Time</span>
                  <input type="time" value={form.startTime} onChange={(event) => updateField('startTime', event.target.value)} />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>End Time</span>
                  <input type="time" value={form.endTime} onChange={(event) => updateField('endTime', event.target.value)} />
                </label>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Recurrence</span>
                  <select value={form.recurrenceType} onChange={(event) => updateRecurrenceType(event.target.value as RecurrenceType)}>
                    <option value="None">None</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                </label>

                {form.recurrenceType !== 'None' && (
                  <div style={{ display: 'grid', gap: 10, padding: 12, border: '1px solid #dfe8f8', borderRadius: 10, background: '#f8fbff' }}>
                    <div style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, color: '#475569' }}>Repeat every</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <input
                          type="number"
                          min={1}
                          value={form.recurrenceRule?.interval || '1'}
                          onChange={(event) => updateRecurrenceInterval(event.target.value)}
                          style={{ width: 90 }}
                        />
                        <span style={{ fontSize: 13, color: '#334155' }}>
                          {form.recurrenceType === 'Daily' ? 'day(s)' : form.recurrenceType === 'Weekly' ? 'week(s)' : 'month(s)'}
                        </span>
                      </div>
                    </div>

                    {form.recurrenceType === 'Weekly' && (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>On these weekdays</span>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8 }}>
                          {WEEKDAY_OPTIONS.map((option) => {
                            const currentRule = form.recurrenceRule || { interval: '1', weekDays: getDefaultWeekDays(form.startDate), monthDay: '' };
                            const selectedDays = Array.isArray(currentRule.weekDays)
                              ? currentRule.weekDays.map((day) => Number(day))
                              : getDefaultWeekDays(form.startDate);
                            const isSelected = selectedDays.includes(option.value);

                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => toggleRecurrenceWeekDay(option.value)}
                                style={{
                                  border: `1px solid ${isSelected ? '#2563eb' : '#dfe8f8'}`,
                                  background: isSelected ? '#dbeafe' : '#fff',
                                  color: '#1e293b',
                                  borderRadius: 8,
                                  padding: '8px 0',
                                  cursor: 'pointer',
                                  fontWeight: isSelected ? 700 : 500,
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {form.recurrenceType === 'Monthly' && (
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#475569' }}>Day of month</span>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={form.recurrenceRule?.monthDay || getDefaultMonthDay(form.startDate)}
                          onChange={(event) => updateRecurrenceMonthDay(event.target.value)}
                          style={{ width: 120 }}
                        />
                      </label>
                    )}
                  </div>
                )}

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Location Type</span>
                  <select value={form.locationType} onChange={(event) => updateField('locationType', event.target.value as LocationType)}>
                    <option value="InPerson">In Person</option>
                    <option value="Online">Online</option>
                  </select>
                </label>
              </div>

              {form.locationType === 'InPerson' ? (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Location</span>
                  <input type="text" value={form.location} onChange={(event) => updateField('location', event.target.value)} placeholder="Room or address" />
                </label>
              ) : (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Meeting URL</span>
                  <input type="url" value={form.meetingUrl} onChange={(event) => updateField('meetingUrl', event.target.value)} placeholder="https://..." />
                </label>
              )}

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Description</span>
                <textarea value={form.description} onChange={(event) => updateField('description', event.target.value)} rows={3} />
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Notes</span>
                <textarea value={form.notes} onChange={(event) => updateField('notes', event.target.value)} rows={3} />
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="amd-btn amd-btn-ghost" onClick={() => navigate('/manage-schedule')}>
                  Cancel
                </button>
                <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void onSubmit(); }} disabled={saving}>
                  {saving ? 'Saving...' : isCreateMode ? 'Create Schedule' : 'Save Changes'}
                </button>
              </div>
                </div>
              )}

              {activeTab === 'sessions' && (
                <ScheduleSessionsPanel
                  sessions={filteredSessions}
                  mode="tab"
                  onUpdateSession={onUpdateSession}
                  onStartSession={onStartSession}
                  onCompleteSession={onCompleteSession}
                  onRecordAttendance={onRecordAttendance}
                />
              )}

              {activeTab === 'calendar' && (
                <ScheduleSessionsCalendar sessions={filteredSessions} />
              )}
            </div>

            <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 18, display: 'grid', gap: 12, alignContent: 'start' }}>
              <h3 style={{ margin: 0, color: '#173159' }}>Summary</h3>
              <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                <div><strong>Title:</strong> {form.title || '—'}</div>
                <div><strong>Target:</strong> {form.targetType}</div>
                <div><strong>Classroom:</strong> {form.targetType === 'Classroom' ? (classrooms.find((item) => String(item.id) === form.classroomId)?.name || '—') : 'Student schedule'}</div>
                <div><strong>Teacher:</strong> {teachers.find((item) => String(item.userId) === form.teacherUserId)?.displayName || '—'}</div>
                <div><strong>Selected students:</strong> {form.targetType === 'Students' ? form.studentUserIds.length : '—'}</div>
                <div><strong>Starts:</strong> {formatDisplayDate(form.startDate ? `${form.startDate}T${form.startTime || '00:00'}:00` : null)}</div>
                <div><strong>Ends:</strong> {formatDisplayDate(form.endDate ? `${form.endDate}T${form.endTime || '00:00'}:00` : null)}</div>
                <div><strong>Recurrence:</strong> {form.recurrenceType === 'None' ? 'None' : `${form.recurrenceType} every ${form.recurrenceRule?.interval || 1}`}</div>
                <div><strong>Location:</strong> {form.locationType === 'Online' ? (form.meetingUrl || 'Online') : (form.location || 'In Person')}</div>
              </div>

              {schedule && (
                <div style={{ borderTop: '1px solid #edf2fb', paddingTop: 12, display: 'grid', gap: 8, fontSize: 13 }}>
                  <div><strong>Status:</strong> {schedule.status || 'Scheduled'}</div>
                  <div><strong>Active:</strong> {schedule.isActive ? 'Yes' : 'No'}</div>
                  <div><strong>Created:</strong> {formatDisplayDate(schedule.startDate)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageScheduleDetail;
